import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface DatePickerProps {
  label?: string;
  value?: Date | null;
  onChange?: (date: Date | undefined) => void;
  placeholder?: string;
  dateFormat?: string;
  disabled?: boolean;
  className?: string;
  id?: string;
  /** Earliest selectable day (inclusive). Days before it are disabled. */
  minDate?: Date;
  /** Latest selectable day (inclusive). Days after it are disabled. */
  maxDate?: Date;
}

export function DatePicker({
  label,
  value,
  onChange,
  placeholder,
  dateFormat = 'PPP',
  disabled = false,
  className,
  id,
  minDate,
  maxDate
}: DatePickerProps) {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder || t('welcome.selectdates.label');
  // Two separate matchers, not one `{ before, after }` object: that shape is
  // react-day-picker's *interval* matcher and would disable the days between the
  // bounds — exactly the opposite of a min/max.
  const disabledDays = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];

  return (
    <div className={cn('flex flex-col gap-2', className)}>
      {label && <Label htmlFor={id}>{label}</Label>}
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id={id}
            variant="outline"
            disabled={disabled}
            className={cn('w-full justify-start text-left font-normal', !value && 'text-muted-foreground')}>
            <CalendarIcon className="mr-2 h-4 w-4" />
            {value ? format(value, dateFormat) : resolvedPlaceholder}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0">
          <Calendar
            mode="single"
            selected={value || undefined}
            onSelect={onChange}
            disabled={disabledDays.length ? disabledDays : undefined}
            startMonth={minDate}
            endMonth={maxDate}
            defaultMonth={value || maxDate || undefined}
            initialFocus
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
